import {
    IAppConfig,
    IStore,
    InsuranceType,
    IUpdateOfferResponse,
    ICoverageModel,
    ICoverage,
    ISelectedCoverage
} from '../type';
import {
    setConfig,
    setFormStep,
    setModel,
    setResources,
    setUiStatus,
    setUpdatingCoverage,
    setShowNewInsuranceStartDateInputType,
    setHasContentInsurance,
    setInsuranceShouldTerminate,
    updateModel,
    setDataPolicyContentBlock,
    setTerminationFeeContentBlock,
    setIsPaymentValid
} from './actions';
import { createOffer, getResources, updateOffer, getReceipt, submitOffer, getContentBlocks } from './api';
import {
    selectCurrentStep,
    selectCustomerInfoIsValid,
    selectPaymentInfoIsValid,
    selectResources,
    selectSelectedCoverages,
    selectUiStatus,
    selectCurrentInsurance,
    selectCurrentInsuranceTypeIsValid,
    selectModel,
    selectCoverages,
    selectPrimaryType
} from './selectors';

export const log = (level: 'debug' | 'info' | 'warn' | 'error', msg: string) => ({
    type: 'log',
    meta: {
        logLevel: level,
        logMessage: msg
    }
});

const trackEvent = (title: string) => ({
    type: 'track',
    meta: {
        track: 'clickNext',
        trackData: {
            buyInsuranceFlow: title
        }
    }
});

function updateCoverages(coverages: ICoverage[], chosenCoverageIds?: number[]) {
    if (!chosenCoverageIds) {
        return coverages;
    }

    return coverages.map(coverage => {
        coverage.chosen = chosenCoverageIds.includes(coverage.objectCoverageId);
        return coverage;
    });
}

function getUpdatedUiModel(store: IStore, updatedModel: IUpdateOfferResponse): ICoverageModel {
    const model = selectModel(store);
    const { dealPrice, disabilityCoverageAmount, chosenCoverageIds } = updatedModel;

    if (!model) throw new Error('Missing model in state');

    if (disabilityCoverageAmount) {
        return {
            ...model,
            dealPrice,
            disabilityCoverageAmount
        };
    }

    return {
        ...model,
        dealPrice,
        coverages: {
            accident: updateCoverages(model.coverages.accident, chosenCoverageIds),
            contents: updateCoverages(model.coverages.contents, chosenCoverageIds)
        }
    };
}

function getCoverage(coverages: { accident: ICoverage[]; contents: ICoverage[] }, objectCoverageId: number) {
    const selectedCoverage =
        coverages.accident.find(coverage => coverage.objectCoverageId === objectCoverageId) ||
        coverages.contents.find(coverage => coverage.objectCoverageId === objectCoverageId);

    if (selectedCoverage) {
        return selectedCoverage;
    }

    return null;
}

function formatSelectedCoverage(coverage: ICoverage, objectCoverageId: number) {
    return {
        objectCoverageId,
        objectId: coverage.objectId,
        policyEnum: coverage.policyEnum,
        policyOfferId: coverage.policyOfferId
    };
}

function trimContentBlock(contentBlock: string) {
    const startIndex = contentBlock.indexOf('<aside');
    const endIndex = contentBlock.lastIndexOf('</aside>');

    if (startIndex !== -1 && endIndex !== -1) {
        return contentBlock.slice(startIndex, endIndex + 8);
    }

    return '';
}

export const errorOut = (error: Error) => (dispatch: any) => {
    dispatch(setUiStatus('error'));
    dispatch(log('error', error.toString()));
};

export const initApp = (config: IAppConfig, isReceiptPage: boolean) => (dispatch: any, getState: () => IStore) => {
    dispatch(setConfig(config));
    dispatch(log('debug', 'Initializing app'));

    const state = getState();

    if (selectUiStatus(state) === 'initializing') {
        const apiCalls = isReceiptPage
            ? getResources(state).then(resources => dispatch(setResources(resources)))
            : Promise.all([
                  getResources(state).then(resources => {
                      const { dataPolicyContentBlockPath, terminationFeeContentBlockPath } = resources;

                      if (dataPolicyContentBlockPath) {
                          getContentBlocks(state, dataPolicyContentBlockPath).then(contentBlock =>
                              dispatch(setDataPolicyContentBlock(trimContentBlock(contentBlock)))
                          );
                      }

                      if (terminationFeeContentBlockPath) {
                          getContentBlocks(state, terminationFeeContentBlockPath).then(contentBlock =>
                              dispatch(setTerminationFeeContentBlock(trimContentBlock(contentBlock)))
                          );
                      }

                      dispatch(setResources(resources));
                  }),
                  createOffer(state).then(model => dispatch(setModel(model)))
              ]);

        return apiCalls
            .then(() => {
                dispatch(setUiStatus('idle'));
                dispatch(log('debug', 'Done initializing'));
            })
            .catch(err => dispatch(errorOut(err)));
    }
    return Promise.resolve();
};

export const focusSection = (step: number) => (dispatch: any, getState: () => IStore) => {
    if (selectCurrentStep(getState()) !== step) {
        dispatch(setFormStep(step));
    }
};

export const toggleCoverageSelection = (objectCoverageId: number) => (dispatch: any, getState: () => IStore) => {
    dispatch(focusSection(1));
    dispatch(setUpdatingCoverage(objectCoverageId));

    const state = getState();

    const selectedCoverages = selectSelectedCoverages(state);

    let newSelectedCoverages: ISelectedCoverage[];

    const coverages = selectCoverages(state);

    if (!coverages) throw new Error('Missing coverages in state');

    const toggledCoverage = getCoverage(coverages, objectCoverageId);

    if (!toggledCoverage) throw new Error('Missing selected coverage in state');

    if (selectedCoverages.findIndex(coverage => coverage.objectCoverageId === objectCoverageId) === -1) {
        const additionalCoverages = [formatSelectedCoverage(toggledCoverage, objectCoverageId)];

        if (toggledCoverage.nameId === 'udvidet-elektronik') {
            const relatedCoverage = coverages.contents.find(coverage => coverage.nameId === 'kortslutning-mm.');

            if (relatedCoverage) {
                additionalCoverages.push(formatSelectedCoverage(relatedCoverage, relatedCoverage.objectCoverageId));
            }
        }

        newSelectedCoverages = selectedCoverages.concat(additionalCoverages);
    } else {
        const removedCoverageIds = [objectCoverageId];

        if (toggledCoverage.nameId === 'udvidet-elektronik') {
            const relatedCoverage = coverages.contents.find(coverage => coverage.nameId === 'kortslutning-mm.');

            if (relatedCoverage) {
                removedCoverageIds.push(relatedCoverage.objectCoverageId);
            }
        }

        newSelectedCoverages = selectedCoverages.filter(
            coverage => !removedCoverageIds.includes(coverage.objectCoverageId)
        );
    }

    return updateOffer(state, {
        selectedCoverages: newSelectedCoverages
    })
        .then(updatedModel => {
            dispatch(updateModel(getUpdatedUiModel(state, updatedModel)));
            dispatch(setUpdatingCoverage(null));
        })
        .catch(err => dispatch(errorOut(err)));
};

export const selectInjuryAmount = (disabilityCoverageAmount: number) => (dispatch: any, getState: () => IStore) => {
    dispatch(focusSection(1));
    dispatch(setUpdatingCoverage(-2000)); // use a mock number to disable

    const state = getState();

    return updateOffer(state, { disabilityCoverageAmount })
        .then(updatedModel => {
            dispatch(updateModel(getUpdatedUiModel(state, updatedModel)));
            dispatch(setUpdatingCoverage(null));
        })
        .catch(err => dispatch(errorOut(err)));
};

export const completeCoverageSettings = () => (dispatch: any, getState: () => IStore) => {
    const state = getState();

    dispatch(setFormStep(2));
    dispatch(trackEvent(selectResources(state).coverageSectionHeading));
};

export const setExistingContentInsurance = (insuranceType: InsuranceType, hasContentInsurance: boolean) => (
    dispatch: any
) => {
    dispatch(setHasContentInsurance(hasContentInsurance));
    dispatch(setShouldTerminateAndShowDateInput(insuranceType, hasContentInsurance));
};

export const setShouldTerminateAndShowDateInput = (insuranceType: InsuranceType, shouldTerminate: boolean) => (
    dispatch: any,
    getState: () => IStore
) => {
    const state = getState();
    const currentInsurance = selectCurrentInsurance(state);

    if (shouldTerminate) {
        if (currentInsurance.showNewInsuranceStartDateInputType === insuranceType) {
            const otherInsuranceType: InsuranceType = insuranceType === 'accident' ? 'contents' : 'accident';

            if (currentInsurance[otherInsuranceType].shouldTerminate) {
                dispatch(setShowNewInsuranceStartDateInputType(null));
            } else if (currentInsurance[otherInsuranceType].shouldTerminate === false) {
                dispatch(setShowNewInsuranceStartDateInputType(otherInsuranceType));
            }
        }
    } else {
        if (!currentInsurance.showNewInsuranceStartDateInputType) {
            dispatch(setShowNewInsuranceStartDateInputType(insuranceType));
        }
    }

    dispatch(setInsuranceShouldTerminate(insuranceType, shouldTerminate));
};

export const completeCurrentInsuranceInfo = () => (dispatch: any, getState: () => IStore) => {
    const state = getState();

    if (selectCurrentInsuranceTypeIsValid(state, selectPrimaryType(state))) {
        dispatch(setFormStep(3));
        dispatch(trackEvent(selectResources(state).currentInsuranceSectionHeading));
    }
};

export const completeCustomerInfo = () => (dispatch: any, getState: () => IStore) => {
    const state = getState();

    if (selectCustomerInfoIsValid(state)) {
        dispatch(setFormStep(4));
        dispatch(trackEvent(selectResources(state).customerInfoSectionHeading));
    }
};

export const completePaymentInfo = () => (dispatch: any, getState: () => IStore) => {
    const state = getState();
    const model = selectModel(state);
    const resources = selectResources(state);
    const primaryType = selectPrimaryType(state);

    if (model && selectPaymentInfoIsValid(state)) {
        dispatch(setUiStatus('submitting'));
        dispatch(log('debug', 'Submitting...'));
        submitOffer(state, model)
            .then(res => {
                if (res.objectId) {
                    dispatch(log('debug', `Submit success with response: ${JSON.stringify(res)}`));
                    window.location.replace(`${resources[`${primaryType}ReceiptUrl`]}?id=${res.objectId}`);
                } else {
                    dispatch(setIsPaymentValid(false));
                    dispatch(setUiStatus('idle'));
                    dispatch(log('debug', `Submit failed with response ${JSON.stringify(res)}`));
                }
            })
            .catch(err => dispatch(errorOut(err)));
    }
};

export const initReceiptPage = () => (_: any, getState: () => IStore) => {
    const state = getState();
    return getReceipt(state);
};
